<!--
  - Copyright (c) 2008-2023 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
  - This is CONFIDENTIAL code. Use is subject to license terms.
  -->
<script>
export default {
  name: "MeetingTypesMixin",

  data() {
    return {
      supportedMeetingTypes: {
        meeting: {
          type: "MEETING",
          title: "defaultTitleMeeting",
          color: "#218266",
          icon: ["fas", "users"],
          unsupportedInteractions: ["quiz", "election", "person-election", "list-election", "tag-correlation"],
        },
        quiz: {
          type: "QUIZ",
          title: "defaultTitleQuiz",
          color: "#9b65ad",
          icon: ["fas", "trophy"],
          supportedInteractions: ["message", "vote", "imagevote", "quiz", "correlation"],
          points: {
            min: -100,
            max: 100,
          },
        },
        election: {
          type: "ELECTION",
          title: "defaultTitleElection",
          color: "#166284",
          icon: ["fas", "vote-yea"],
          supportedInteractions: [
            "message",
            "text",
            "vote",
            "qa",
            "person-election",
            "list-election",
            "tag-correlation",
          ],
        },
      },
    }
  },

  methods: {
    isAsync(meeting) {
      return meeting && meeting.operationMode === "ASYNC"
    },

    isElection(meeting) {
      return meeting && meeting.type === "ELECTION"
    },

    /**
     * Function returning the i18n name or postfix depending on the specified meeting's type property
     * @return {(string|null)}
     */
    getSupportedMeetingType(meeting) {
      if (!this.getMeetingTypeObject(meeting.type)) {
        return null
      }

      return meeting.type
    },

    getMeetingTypeObject(type) {
      for (const supportedMeetingTypeKey in this.supportedMeetingTypes) {
        const supportedMeetingType = this.supportedMeetingTypes[supportedMeetingTypeKey]

        if (supportedMeetingType.type && supportedMeetingType.type === type) {
          return supportedMeetingType
        }
      }

      return this.supportedMeetingTypes.meeting.type
    },

    /**
     * Returns an array of meeting types of the given main meeting and its breakout meetings. Each type is contained
     * at most once. The returned array is in no particular order.
     *
     * @param mainMeeting The main meeting to collect the types from
     * @return {[]} The meeting types of the passed meeting and its breakout meetings
     */
    getMeetingTypes(mainMeeting) {
      return Array.from(this.getMeetingTypesSet(mainMeeting))
    },

    /**
     * Returns a Set of meeting types of the given main meeting and its breakout meetings. Each type is contained
     * at most once. The returned array is in no particular order.
     *
     * @param mainMeeting The main meeting to collect the types from
     * @return {Set} The meeting types of the passed meeting and its breakout meetings
     */
    getMeetingTypesSet(mainMeeting) {
      let meetingTypes = new Set()
      meetingTypes.add(mainMeeting.type)
      mainMeeting.breakoutMeetings?.forEach((b) => meetingTypes.add(b.type))
      return meetingTypes
    },

    /**
     * Returns an array with the names of the logical types of the breakout meetings whose type is not licensed.
     *
     * @return {String[]} A maybe empty array containing the unlicensed breakout meeting types
     */
    getUnLicensedMeetingTypes(mainMeeting) {
      let meetingTypes = this.getMeetingTypes(mainMeeting)

      // Return those meeting types that don't have at least one meeting room licensed
      return meetingTypes.filter(
        (type) => this.$store.getters["effectiveLicenses/meetingRoomCount"](type, mainMeeting.tenantId) === 0
      )
    },

    /**
     * Returns an array of those meeting types that _currently_ don't have a meeting room available.
     *
     * @param mainMeeting The main meeting that is used for determining the affected meeting types
     * @return {String[]}
     */
    getUnavailableMeetingTypes(mainMeeting) {
      let tenantId = mainMeeting.tenantId
      let meetingTypes = this.getMeetingTypes(mainMeeting)

      // Return array of those meeting types that don't have any available meeting room
      return meetingTypes.filter((type) => {
        let openMeetingsOfType = this.$store.getters["meetings/getOpenMeetingCount"](type, tenantId)
        if (openMeetingsOfType === null) {
          return true // We assume a meeting is available if the data isn't loaded yet
        }
        let meetingRoomsLicensed = this.$store.getters["effectiveLicenses/meetingRoomCount"](type, tenantId)

        return meetingRoomsLicensed > -1 && openMeetingsOfType >= meetingRoomsLicensed
      })
    },
  },

  i18n: {
    messages: {
      de: {
        defaultTitleMeeting: "Neues Meeting",
        defaultTitleElection: "Neue Veranstaltung",
        defaultTitleQuiz: "Neues Quiz",
        infoTitle: {
          MEETING: "Informationen zum Meeting",
          QUIZ: "Informationen zum Quiz",
          ELECTION: "Informationen zur Veranstaltung",
        },
        usersWithAccess: "Nutzer mit Zugriff",
        usersWithAccessCount: "Nicht geteilt | Zugriff hat ein weiterer Nutzer | Zugriff haben {count} weitere Nutzer",
        ownerTooltip: {
          MEETING: "Dieses Meeting ist im Besitz von {owner}. Klicken, um es einem anderen Nutzer zu übertragen.",
          ELECTION:
            "Diese Veranstaltung ist im Besitz von {owner}. Klicken, um sie einem anderen Nutzer zu übertragen.",
          QUIZ: "Dieses Quiz ist im Besitz von {owner}. Klicken, um es einem anderen Nutzer zu übertragen.",
        },
        ownerTooltipUnassigned: {
          MEETING: "Dieses Meeting ist keinem Eigentümer zugewiesen. Klicken, um es zuzuweisen.",
          ELECTION: "Diese Veranstaltung ist keinem Eigentümer zugewiesen. Klicken, um sie zuzuweisen.",
          QUIZ: "Dieses Quiz ist keinem Eigentümer zugewiesen. Klicken, um es zuzuweisen.",
        },
        collaboratorTooltip: {
          MEETING:
            "Dieses Meeting wird mit keinem Nutzer geteilt. Klicken, um es zu teilen. | Dieses Meeting wird mit einem Nutzer geteilt. | Dieses Meeting wird mit {count} Nutzern geteilt.",
          ELECTION:
            "Diese Veranstaltung wird mit keinem Nutzer geteilt. Klicken, um sie zu teilen. | Diese Veranstaltung wird mit einem Nutzer geteilt. | Diese Veranstaltung wird mit {count} Nutzern geteilt.",
          QUIZ: "Dieses Quiz wird mit keinem Nutzer geteilt. Klicken, um es zu teilen. | Dieses Quiz wird mit einem Nutzer geteilt. Klicken, um es zu teilen. | Dieses Quiz wird mit {count} Nutzern geteilt.",
        },
        meetingSharingText: {
          MEETING:
            "Hier können Sie konfigurieren, welche Benutzer Zugriff auf welche Teile dieses Meetings und etwaiger Breakout-Meetings haben.<br/> Es gibt stets einen Eigentümer. Zusätzlich können Mitbearbeiter hinzugefügt werden, wobei standardmäßig der Zugriff nicht eingeschränkt ist. Um Zugriff für einen Benutzer einzuschränken, klappen Sie den entsprechenden Bereich aus und nehmen die gewünschten Änderungen vor.",
          ELECTION:
            "Hier können Sie konfigurieren, welche Benutzer Zugriff auf welche Teile dieser Veranstaltung und etwaiger Breakout-Veranstaltungen haben.<br/> Es gibt stets einen Eigentümer. Zusätzlich können Mitbearbeiter hinzugefügt werden, wobei standardmäßig der Zugriff nicht eingeschränkt ist. Um Zugriff für einen Benutzer einzuschränken, klappen Sie den entsprechenden Bereich aus und nehmen die gewünschten Änderungen vor.",
          QUIZ: "Hier können Sie konfigurieren, welche Benutzer Zugriff auf welche Teile dieses Quiz und etwaiger Breakout-Quiz haben.<br/> Es gibt stets einen Eigentümer. Zusätzlich können Mitbearbeiter hinzugefügt werden, wobei standardmäßig der Zugriff nicht eingeschränkt ist. Um Zugriff für einen Benutzer einzuschränken, klappen Sie den entsprechenden Bereich aus und nehmen die gewünschten Änderungen vor.",
        },
        meetingSharingTableEmpty: {
          MEETING: "Dieses Meeting wird aktuell nicht geteilt.",
          ELECTION: "Diese Veranstaltung wird aktuell nicht geteilt.",
          QUIZ: "Dieses Quiz wird aktuell nicht geteilt.",
        },
        participantSetup: {
          MEETING: "Bitte richten Sie die Teilnehmer Ihres Meetings ein",
          ELECTION: "Bitte richten Sie die Teilnehmer Ihrer Veranstaltung ein",
          QUIZ: "Bitte richten Sie die Teilnehmer Ihres Quiz ein",
        },
        interactionTitle: {
          MEETING: "Meeting-Interaktionen",
          ELECTION: "Interaktionen",
          QUIZ: "Quiz-Interaktionen",
        },
        resultTitle: {
          MEETING: "Meeting-Ergebnisse",
          ELECTION: "Ergebnisse",
          QUIZ: "Quiz-Ergebnisse",
        },
        joinCodeInfo: {
          MEETING: "Ihr teambits Meeting hat den Zugangscode",
          ELECTION: "Ihre Veranstaltung hat den Zugangscode",
          QUIZ: "Ihr Quiz hat den Zugangscode",
        },
        titleLabel: {
          MEETING: "Veranstaltungstitel",
          ELECTION: "Veranstaltungstitel",
          QUIZ: "Quiztitel",
          default: "Titel",
        },
        titleText: {
          MEETING: "Meine neue Veranstaltung",
          ELECTION: "Meine neue Veranstaltung",
          QUIZ: "Mein neues Quiz",
        },
        resetConfirmTitle: {
          MEETING: "Meeting wird zurückgesetzt",
          ELECTION: "Veranstaltung wird zurückgesetzt",
          QUIZ: "Quiz wird zurückgesetzt",
        },
        resetConfirmText: {
          MEETING:
            "Sämtliche Beiträge des folgenden Meetings werden gelöscht. Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
          ELECTION:
            "Sämtliche Beiträge der folgenden Veranstaltung werden gelöscht. Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
          QUIZ: "Sämtliche Beiträge des folgenden Quiz werden gelöscht. Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
        },
        deleteConfirmTitle: {
          MEETING: "Meeting wird gelöscht",
          ELECTION: "Veranstaltung wird gelöscht",
          QUIZ: "Quiz wird gelöscht",
        },
        deleteConfirmText: {
          MEETING:
            "Das folgende Meeting wird gelöscht. Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
          ELECTION:
            "Die folgende Veranstaltung wird gelöscht. Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
          QUIZ: "Das folgende Quiz wird gelöscht. Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
        },
        deleteConfirmTextMulti: {
          MEETING:
            "Das folgende Meeting und <b>alle zugehörigen Breakout-Meetings</b> werden gelöscht.<br/>Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
          QUIZ: "Das folgende Quiz und <b>alle zugehörigen Breakout-Meetings</b> werden gelöscht.<br/>Diese Operation kann nicht rückgängig gemacht werden - sind Sie sicher?",
        },
        prepare: {
          MEETING: "Ihr Meeting wird erstellt...",
          ELECTION: "Ihre Veranstaltung wird erstellt...",
          QUIZ: "Ihr Quiz wird erstellt...",
        },
      },
      en: {
        defaultTitleMeeting: "New meeting",
        defaultTitleElection: "New election",
        defaultTitleQuiz: "New quiz",
        infoTitle: {
          MEETING: "Information about this meeting",
          ELECTION: "Information about this election",
          QUIZ: "Information about this quiz",
        },
        usersWithAccess: "Users with access",
        usersWithAccessCount: "Not shared | Shared with another user | Shared with {count} other users",
        ownerTooltip: {
          MEETING: "This meeting is owned by {owner}. Click to transfer it to another user.",
          ELECTION: "This election is owned by {owner}. Click to transfer it to another user.",
          QUIZ: "This quiz is owned by {owner}. Click to transfer it to another user.",
        },
        ownerTooltipUnassigned: {
          MEETING: "This meeting is not assigned to an owner. Click to assign it.",
          ELECTION: "This election is not assigned to an owner. Click to assign it.",
          QUIZ: "This quiz is not assigned to an owner. Click to assign it.",
        },
        collaboratorTooltip: {
          MEETING:
            "This meeting is shared with no user. Click to share it. | This meeting is shared with one user. | This meeting is shared with {count} users.",
          ELECTION:
            "This election is shared with no user. Click to share it. | This election is shared with one user. | This election is shared with {count} users.",
          QUIZ: "This quiz is shared with no user. Click to share it. | This quiz is shared with one user. | This quiz is shared with {count} users.",
        },
        meetingSharingText: {
          MEETING:
            "Here you can configure which users have access to which parts of this meeting and any breakout meetings.<br/> There is always one owner. Additionally, collaborators can be added and by default access is not restricted. To restrict access for a user, unfold the appropriate area and make the desired changes.",
          ELECTION:
            "Here you can configure which users have access to which parts of this election and any breakout elections.<br/> There is always one owner. Additionally, collaborators can be added and by default access is not restricted. To restrict access for a user, unfold the appropriate area and make the desired changes.",
          QUIZ: "Here you can configure which users have access to which parts of this quiz and any breakout quizzes.<br/> There is always one owner. Additionally, collaborators can be added and by default access is not restricted. To restrict access for a user, unfold the appropriate area and make the desired changes.",
        },
        meetingSharingTableEmpty: {
          MEETING: "This meeting is currently not shared.",
          ELECTION: "This election is currently not shared.",
          QUIZ: "This quiz is currently not shared.",
        },
        participantSetup: {
          MEETING: "Please setup participants of your meeting",
          ELECTION: "Please setup participants of your election",
          QUIZ: "Please setup participants of your quiz",
        },
        interactionTitle: {
          MEETING: "Meeting Interactions",
          ELECTION: "Election Interactions",
          QUIZ: "Quiz Interactions",
        },
        resultTitle: {
          MEETING: "Meeting Results",
          ELECTION: "Election Results",
          QUIZ: "Quiz Results",
        },
        joinCodeInfo: {
          MEETING: "Your meeting may be entered with access code",
          ELECTION: "Your election may be entered with access code",
          QUIZ: "Your quiz may be entered with access code",
        },
        titleLabel: {
          MEETING: "Meeting title",
          ELECTION: "Election title",
          QUIZ: "Quiz title",
          default: "Title",
        },
        titleText: {
          MEETING: "My new meeting",
          ELECTION: "My new election",
          QUIZ: "My new quiz",
        },
        resetConfirmTitle: {
          MEETING: "Meeting will be reset",
          ELECTION: "Election will be reset",
          QUIZ: "Quiz will be reset",
        },
        resetConfirmText: {
          MEETING:
            "All contributions of the following meeting will be deleted. This operation cannot be undone - are you sure?",
          ELECTION:
            "All contributions of the following election will be deleted. This operation cannot be undone - are you sure?",
          QUIZ: "All contributions of the following quiz will be deleted. This operation cannot be undone - are you sure?",
        },
        deleteConfirmTitle: {
          MEETING: "Meeting will be removed",
          ELECTION: "Election will be removed",
          QUIZ: "Quiz will be removed",
        },
        deleteConfirmText: {
          MEETING: "The following meeting will be removed. This operation cannot be undone - are you sure?",
          ELECTION: "The following election will be removed. This operation cannot be undone - are you sure ?",
          QUIZ: "The following quiz will be removed. This operation cannot be undone - are you sure?",
        },
        deleteConfirmTextMulti: {
          MEETING:
            "The following meeting <b>and all related breakout meetings</b> will be removed.<br/>This operation cannot be undone - are you sure?",
          QUIZ: "The following quiz and <b>all related breakout meetings</b> will be removed.<br/>This operation cannot be undone - are you sure?",
        },
        prepare: {
          MEETING: "Your meeting is being prepared...",
          ELECTION: "Your election is being prepared...",
          QUIZ: "Your quiz is being prepared...",
        },
      },
    },
  },
}
</script>
